/** @format */
import Cookie from 'js-cookie';
import { useRouter } from 'next/router';
import React, { useContext, useEffect } from 'react';

import {
    Market,
    MarketCode,
    primaryMarketsInMultiMarketCountry,
} from '@toucan/config/markets';

import { LayoutType } from '@toucan/components/organisms/layout';
import { MarketConfirmationModal } from '@toucan/components/organisms/layout/hooks/use-confirm-market-dialog/components/market-confirmation-modal';

import {
    ApplicationContext,
    ModalSize,
} from '@toucan/client/global/application';
import { MarketContext } from '@toucan/client/global/market';
import { UserContext } from '@toucan/client/global/user/context';

export function checkAndDisplayMarketConfirmation(
    activeMarket: Market,
    showMarketConfirmation: string | string[] | undefined,
    showConfirmation: (alternativeMarkets: MarketCode[]) => void,
    geolocatedMarket?: MarketCode
) {
    // saved preference cookie
    const mpbMarketCookieValue = Cookie.get(
        'mpb_market'
    )?.toUpperCase() as MarketCode;

    const shouldShowMarketSelector = mpbMarketCookieValue !== activeMarket.code;

    // never show the selector if the mpb_market cookie matches the active market
    if (!shouldShowMarketSelector) {
        return;
    }

    // user has a preference set that is different to the active market, check against that.
    if (mpbMarketCookieValue) {
        return showConfirmation([mpbMarketCookieValue]);
    }

    if (!geolocatedMarket) return;

    const geolocatedMarketMatchesActiveMarket =
        geolocatedMarket === activeMarket.code;

    // user is geolocated to the active market - do nothing
    if (!showMarketConfirmation && geolocatedMarketMatchesActiveMarket) {
        return;
    }

    const alternativeMarketsInSameCountry: MarketCode[] | undefined =
        // @ts-ignore - Element implicitly has an any type - Touch and Change
        primaryMarketsInMultiMarketCountry?.[geolocatedMarket];

    // user has been geolocated to market that has multiple markets in the same country
    if (
        !showMarketConfirmation &&
        alternativeMarketsInSameCountry?.length &&
        !alternativeMarketsInSameCountry.includes(activeMarket.code)
    ) {
        return showConfirmation([
            geolocatedMarket,
            ...alternativeMarketsInSameCountry,
        ]);
    }

    // user is geolocated to the primary market of a market that has multiple in the same country
    // and the showConfirmation query param is present this means they were redirected
    // from mpb.com/ with no market slug
    if (showMarketConfirmation && alternativeMarketsInSameCountry?.length) {
        return showConfirmation([...alternativeMarketsInSameCountry]);
    }

    const isNonPrimaryMarket = Object.keys(
        primaryMarketsInMultiMarketCountry
    ).some((marketCode) => (marketCode as MarketCode) === geolocatedMarket);

    // user is in non primary market and has been geolocated to it's primary market - do nothing
    if (isNonPrimaryMarket) {
        return;
    }

    // check against geolocated market
    return showConfirmation([geolocatedMarket]);
}

export const useConfirmMarketDialog = (layoutType: LayoutType) => {
    const { activeMarket } = useContext(MarketContext);
    const router = useRouter();
    const { showMarketConfirmation } = router.query;

    const { setModalContent, setModalVisible, userAgentDetectedAsBot } =
        useContext(ApplicationContext);
    const { geolocatedMarket } = useContext(UserContext);

    const showMarketSelector = (alternativeMarkets: MarketCode[]) => {
        const onModalClose = (marketCode: MarketCode) => {
            Cookie.set('mpb_market', marketCode);

            setModalVisible(false);

            if (showMarketConfirmation) {
                const updatedQuery = router.query;
                delete updatedQuery.showMarketConfirmation;

                router.replace({ query: updatedQuery }, undefined, {
                    shallow: true,
                });
            }
        };

        setModalContent(
            <MarketConfirmationModal
                suggestedMarketCodes={alternativeMarkets}
                onClose={onModalClose}
            />
        );

        setModalVisible(true, {
            size: ModalSize.MEDIUM,
            onClose: () => onModalClose(activeMarket.code),
        });
    };

    useEffect(() => {
        if (layoutType !== LayoutType.FULL || userAgentDetectedAsBot) {
            return;
        }

        checkAndDisplayMarketConfirmation(
            activeMarket,
            showMarketConfirmation,
            showMarketSelector,
            geolocatedMarket
        );
    }, [geolocatedMarket]);
};
